import lodash, { toUpper } from 'lodash';
import moment from 'moment';

import { reportTypes, DATE_FORMAT } from './constants';
import { nodeWithArgs, node } from 'common/util/queryHelper';
import styles from '../containers/ReportContent.scss';

export const taskReportColumns = {
    taskTitle: { key: 'taskTitle', label: 'Task Title', filter: 'Filter title' },
    documentName: { key: 'documentName', label: 'Document Name', filter: 'Filter name' },
    sentDate: { key: 'sentDate', label: 'Sent Date', filter: 'Filter date' },
    dueDate: { key: 'dueDate', label: 'Due Date', filter: 'Filter date' },
    reminderDate: { key: 'reminderDate', label: 'Reminder Date', filter: 'Filter date' },
    taskCompletedDate: { key: 'taskCompletedDate', label: 'Task Completed Date', filter: 'Filter date' },
    taskStatus: { key: 'taskStatus', label: 'Task Status', filter: 'Filter status' },
    requestedAction: { key: 'requestedAction', label: 'Requested Action', filter: 'Filter action' },
    fromUserName: { key: 'fromUserName', label: 'Assigned By Name', filter: 'Filter name' },
    fromUserEmail: { key: 'fromUserEmail', label: 'Assigned By Email', filter: 'Filter email' },
    toUserName: { key: 'toUserName', label: 'Recipient Name', filter: 'Filter name' },
    toUserEmail: { key: 'toUserEmail', label: 'Recipient Email', filter: 'Filter email' },
    description: { key: 'description', label: 'Description', filter: 'Filter description' },
    binderTitle: { key: 'binderTitle', label: 'Binder/Study Name', filter: 'Filter binder/study name' },
    binderStatus: { key: 'binderStatus', label: 'Binder/Study Status', filter: 'Filter binder/study status' },
    taskId: { key: 'taskId', label: 'Task ID', filter: 'Filter task id' },
};

export const taskReportDefaultColumns = lodash.map(taskReportColumns, 'key');

const taskReportColumnValues = lodash.values(taskReportColumns);
export const taskReportColumnsList = lodash.orderBy(taskReportColumnValues, ['label'], ['asc']);

export const getTasksReportNode = (args) => {
    return nodeWithArgs('getTasksReportRawData', args, [
        node('rows', [
            'taskTitle',
            'sentDate',
            'dueDate',
            'reminderDate',
            'taskCompletedDate',
            'taskStatus',
            'requestedAction',
            'description',
            'fromUserName',
            'fromUserEmail',
            'toUserName',
            'toUserEmail',
            'documentName',
            'binderTitle',
            'binderStatus',
            'taskId'
        ]),
        'count',
    ]);
};

const taskStatusLookupOptions = {
    INCOMPLETE: 'INCOMPLETE',
    COMPLETED: 'COMPLETED',
    CANCELED: 'CANCELED'
};

const getDefaultFilter = (collection, name) => {
    const findItem = lodash.find(collection, { fieldName: name });
    return findItem ? findItem.value : '';
};

export const formatMaterialTableTasksColumns = (customizeColumns, filters) => {
    return [
        {
            title: taskReportColumns.taskTitle.label,
            field: taskReportColumns.taskTitle.key,
            filterPlaceholder: taskReportColumns.taskTitle.filter,
            hidden: !customizeColumns.includes(taskReportColumns.taskTitle.key),
            emptyValue: <p className={styles.emptyLabel}>No T.T Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.taskTitle.key),
        },
        {
            title: taskReportColumns.documentName.label,
            field: taskReportColumns.documentName.key,
            filterPlaceholder: taskReportColumns.documentName.filter,
            hidden: !customizeColumns.includes(taskReportColumns.documentName.key),
            emptyValue: <p className={styles.emptyLabel}>No D.N Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.documentName.key),
        },
        {
            title: taskReportColumns.sentDate.label,
            field: taskReportColumns.sentDate.key,
            filterPlaceholder: taskReportColumns.sentDate.filter,
            hidden: !customizeColumns.includes(taskReportColumns.sentDate.key),
            emptyValue: <p className={styles.emptyLabel}>No S.D Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.sentDate.key),
            render: rowData => {
                return moment(rowData[taskReportColumns.sentDate.key]).format(DATE_FORMAT);
            },
        },
        {
            title: taskReportColumns.dueDate.label,
            field: taskReportColumns.dueDate.key,
            filterPlaceholder: taskReportColumns.dueDate.filter,
            hidden: !customizeColumns.includes(taskReportColumns.dueDate.key),
            emptyValue: <p className={styles.emptyLabel}>No D.D Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.dueDate.key),
            render: rowData => {
                return moment(rowData[taskReportColumns.dueDate.key]).format(DATE_FORMAT);
            },
        },
        {
            title: taskReportColumns.reminderDate.label,
            field: taskReportColumns.reminderDate.key,
            filterPlaceholder: taskReportColumns.reminderDate.filter,
            hidden: !customizeColumns.includes(taskReportColumns.reminderDate.key),
            emptyValue: <p className={styles.emptyLabel}>No R.D Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.reminderDate.key),
            render: rowData => {
                return moment(rowData[taskReportColumns.reminderDate.key]).format(DATE_FORMAT);
            },
        },
        {
            title: taskReportColumns.taskCompletedDate.label,
            field: taskReportColumns.taskCompletedDate.key,
            filterPlaceholder: taskReportColumns.taskCompletedDate.filter,
            hidden: !customizeColumns.includes(taskReportColumns.taskCompletedDate.key),
            emptyValue: <p className={styles.emptyLabel}>No T.C.D Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.taskCompletedDate.key),
            render: rowData => {
                return moment(rowData[taskReportColumns.taskCompletedDate.key]).format(DATE_FORMAT);
            },
        },
        {
            title: taskReportColumns.taskStatus.label,
            field: taskReportColumns.taskStatus.key,
            hidden: !customizeColumns.includes(taskReportColumns.taskStatus.key),
            defaultFilter: getDefaultFilter(filters, taskReportColumns.taskStatus.key),
            lookup: taskStatusLookupOptions,
            emptyValue: <p className={styles.emptyLabel}>No T.S Provided</p>,
        },
        {
            title: taskReportColumns.requestedAction.label,
            field: taskReportColumns.requestedAction.key,
            filterPlaceholder: taskReportColumns.requestedAction.filter,
            hidden: !customizeColumns.includes(taskReportColumns.requestedAction.key),
            emptyValue: <p className={styles.emptyLabel}>No R.A Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.requestedAction.key),
        },
        {
            title: taskReportColumns.fromUserName.label,
            field: taskReportColumns.fromUserName.key,
            filterPlaceholder: taskReportColumns.fromUserName.filter,
            hidden: !customizeColumns.includes(taskReportColumns.fromUserName.key),
            emptyValue: <p className={styles.emptyLabel}>No F.U.N Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.fromUserName.key),
        },
        {
            title: taskReportColumns.fromUserEmail.label,
            field: taskReportColumns.fromUserEmail.key,
            filterPlaceholder: taskReportColumns.fromUserEmail.filter,
            hidden: !customizeColumns.includes(taskReportColumns.fromUserEmail.key),
            emptyValue: <p className={styles.emptyLabel}>No F.U.E Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.fromUserEmail.key),
        },
        {
            title: taskReportColumns.toUserName.label,
            field: taskReportColumns.toUserName.key,
            filterPlaceholder: taskReportColumns.toUserName.filter,
            hidden: !customizeColumns.includes(taskReportColumns.toUserName.key),
            emptyValue: <p className={styles.emptyLabel}>No T.U.N Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.toUserName.key),
        },
        {
            title: taskReportColumns.toUserEmail.label,
            field: taskReportColumns.toUserEmail.key,
            filterPlaceholder: taskReportColumns.toUserEmail.filter,
            hidden: !customizeColumns.includes(taskReportColumns.toUserEmail.key),
            emptyValue: <p className={styles.emptyLabel}>No T.U.E Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.toUserEmail.key),
        },
        {
            title: taskReportColumns.description.label,
            field: taskReportColumns.description.key,
            filterPlaceholder: taskReportColumns.description.filter,
            hidden: !customizeColumns.includes(taskReportColumns.description.key),
            emptyValue: <p className={styles.emptyLabel}>No Description Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.description.key),
        },
        {
            title: taskReportColumns.binderTitle.label,
            field: taskReportColumns.binderTitle.key,
            filterPlaceholder: taskReportColumns.binderTitle.filter,
            hidden: !customizeColumns.includes(taskReportColumns.binderTitle.key),
            emptyValue: <p className={styles.emptyLabel}>No Binder Title Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.binderTitle.key),
        },
        {
            title: taskReportColumns.binderStatus.label,
            field: taskReportColumns.binderStatus.key,
            filterPlaceholder: taskReportColumns.binderStatus.filter,
            hidden: !customizeColumns.includes(taskReportColumns.binderStatus.key),
            emptyValue: <p className={styles.emptyLabel}>No Binder Status Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.binderStatus.key),
            render: rowData => {
                return toUpper(rowData[taskReportColumns.binderStatus.key] || '');
            },
        },
        {
            title: taskReportColumns.taskId.label,
            field: taskReportColumns.taskId.key,
            filterPlaceholder: taskReportColumns.taskId.filter,
            hidden: !customizeColumns.includes(taskReportColumns.taskId.key),
            emptyValue: <p className={styles.emptyLabel}>No Task ID Provided</p>,
            defaultFilter: getDefaultFilter(filters, taskReportColumns.taskId.key),
        }
    ];
};

export const reportTaskOptionData = {
    name: reportTypes.task,
    displayName: 'Document Tasks Reports',
    reportType: reportTypes.task,
    parameters: [
        {
            id: 'institutionId',
            choice: 'institutionId',
            isChoiceRequired: true,
            isChoiceHidden: true,
            label: 'Institution Id',
            type: null,
            source: null,
            dataKey: 'institutionId',
            target: 'institutionId',
            submit: true,
        },
        {
            id: 'all',
            choice: 'options',
            choiceLabel: 'Choose which tasks to report on:',
            isChoiceRequired: true,
            isChoiceHidden: false,
            label: 'All document tasks in institution',
            type: null,
            source: null,
            target: null,
            submit: false,
        },
        {
            id: 'documentTypes',
            choice: 'options',
            choiceLabel: 'Choose which tasks to report on',
            isChoiceRequired: true,
            isChoiceHidden: false,
            label: 'All document tasks by document types',
            type: 'multiselect',
            source: 'documentTypes',
            dataKey: 'documentTypes',
            target: 'documentTypeIds',
            targetKey: 'key',
            submit: true,
        },
        {
            id: 'binderIds',
            choice: 'options',
            choiceLabel: 'Choose which tasks to report on:',
            isChoiceRequired: true,
            isChoiceHidden: false,
            label: 'All document tasks by binders',
            type: 'multiselect',
            source: 'binders',
            dataKey: 'binders',
            target: 'binderIds',
            targetKey: 'key',
            submit: true,
        },
    ],
};
